import { AdminLayout } from '@app/layout/AdminLayout';
import { lazy } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import { RequireAuth } from './RequireAuth';
import { RequireLevel, ROLE_LEVELS } from './RequireLevel';

// Lazy loaded pages
const LoginPage = lazy(() => import('@features/auth/LoginPage'));
const DashboardPage = lazy(() => import('@features/dashboard/DashboardPage'));
const PalletListPage = lazy(() => import('@features/pallets/PalletListPage'));
const PalletCreatePage = lazy(() => import('@features/pallets/PalletCreatePage'));
const PalletDetailPage = lazy(() =>
	import('@features/pallets/PalletDetailPage').then((m) => ({ default: m.PalletDetailPage }))
);
const PalletEditPage = lazy(() =>
	import('@features/pallets/PalletEditPage').then((m) => ({ default: m.PalletEditPage }))
);
const PalletHistoryPage = lazy(() =>
	import('@features/pallets/PalletHistoryPage').then((m) => ({ default: m.PalletHistoryPage }))
);
const ProductListPage = lazy(() => import('@features/products/ProductListPage'));
const ProductDetailPage = lazy(() =>
	import('@features/products/ProductDetailPage').then((m) => ({ default: m.ProductDetailPage }))
);
const ProductCreatePage = lazy(() =>
	import('@features/products/ProductCreatePage').then((m) => ({ default: m.ProductCreatePage }))
);
const ProductEditPage = lazy(() =>
	import('@features/products/ProductEditPage').then((m) => ({ default: m.ProductEditPage }))
);
const CategoryListPage = lazy(() =>
	import('@features/categories/CategoryListPage').then((m) => ({ default: m.CategoryListPage }))
);
const CategoryCreatePage = lazy(() =>
	import('@features/categories/CategoryCreatePage').then((m) => ({
		default: m.CategoryCreatePage,
	}))
);
const CategoryDetailPage = lazy(() =>
	import('@features/categories/CategoryDetailPage').then((m) => ({
		default: m.CategoryDetailPage,
	}))
);
const CategoryEditPage = lazy(() =>
	import('@features/categories/CategoryEditPage').then((m) => ({ default: m.CategoryEditPage }))
);
const OrderListPage = lazy(() => import('@features/orders/OrderListPage'));
const OrderCreatePage = lazy(() =>
	import('@features/orders/OrderCreatePage').then((m) => ({ default: m.OrderCreatePage }))
);
const OrderDetailPage = lazy(() =>
	import('@features/orders/OrderDetailPage').then((m) => ({ default: m.OrderDetailPage }))
);
const OrderEditPage = lazy(() =>
	import('@features/orders/OrderEditPage').then((m) => ({ default: m.OrderEditPage }))
);
const LocationListPage = lazy(() => import('@features/locations/LocationListPage'));
const LocationCreatePage = lazy(() =>
	import('@features/locations/LocationCreatePage').then((m) => ({
		default: m.LocationCreatePage,
	}))
);
const SingleLocationCreatePage = lazy(() =>
	import('@features/locations/SingleLocationCreatePage').then((m) => ({
		default: m.SingleLocationCreatePage,
	}))
);
const LocationDetailPage = lazy(() =>
	import('@features/locations/LocationDetailPage').then((m) => ({
		default: m.LocationDetailPage,
	}))
);
const LocationEditPage = lazy(() =>
	import('@features/locations/LocationEditPage').then((m) => ({ default: m.LocationEditPage }))
);
const UserListPage = lazy(() => import('@features/users/UserListPage'));
const UserDetailPage = lazy(() =>
	import('@features/users/UserDetailPage').then((m) => ({ default: m.UserDetailPage }))
);
const UserEditPage = lazy(() =>
	import('@features/users/UserEditPage').then((m) => ({ default: m.UserEditPage }))
);
const UserCreatePage = lazy(() =>
	import('@features/users/UserCreatePage').then((m) => ({ default: m.UserCreatePage }))
);
const StockCountListPage = lazy(() =>
	import('@features/stock-counts/StockCountListPage').then((m) => ({
		default: m.StockCountListPage,
	}))
);
const StockCountDetailPage = lazy(() =>
	import('@features/stock-counts/StockCountDetailPage').then((m) => ({
		default: m.StockCountDetailPage,
	}))
);
const StockCountCreatePage = lazy(() =>
	import('@features/stock-counts/StockCountCreatePage').then((m) => ({
		default: m.StockCountCreatePage,
	}))
);
const MovementListPage = lazy(() =>
	import('@features/movements/MovementListPage').then((m) => ({ default: m.MovementListPage }))
);
const MovementDetailPage = lazy(() =>
	import('@features/movements/MovementDetailPage').then((m) => ({
		default: m.MovementDetailPage,
	}))
);
const ExpiryAlertPage = lazy(() =>
	import('@features/expiry-alerts/ExpiryAlertPage').then((m) => ({
		default: m.ExpiryAlertPage,
	}))
);
const SupplierListPage = lazy(() =>
	import('@features/suppliers/SupplierListPage').then((m) => ({ default: m.SupplierListPage }))
);
const SupplierDetailPage = lazy(() =>
	import('@features/suppliers/SupplierDetailPage').then((m) => ({
		default: m.SupplierDetailPage,
	}))
);
const SupplierEditPage = lazy(() =>
	import('@features/suppliers/SupplierEditPage').then((m) => ({ default: m.SupplierEditPage }))
);
const SupplierCreatePage = lazy(() =>
	import('@features/suppliers/SupplierCreatePage').then((m) => ({
		default: m.SupplierCreatePage,
	}))
);
const CustomerListPage = lazy(() =>
	import('@features/customers/CustomerListPage').then((m) => ({ default: m.CustomerListPage }))
);
const CustomerDetailPage = lazy(() =>
	import('@features/customers/CustomerDetailPage').then((m) => ({
		default: m.CustomerDetailPage,
	}))
);
const CustomerEditPage = lazy(() =>
	import('@features/customers/CustomerEditPage').then((m) => ({ default: m.CustomerEditPage }))
);
const CustomerCreatePage = lazy(() =>
	import('@features/customers/CustomerCreatePage').then((m) => ({
		default: m.CustomerCreatePage,
	}))
);
const WarehouseListPage = lazy(() =>
	import('@features/warehouses/WarehouseListPage').then((m) => ({
		default: m.WarehouseListPage,
	}))
);
const WarehouseDetailPage = lazy(() =>
	import('@features/warehouses/WarehouseDetailPage').then((m) => ({
		default: m.WarehouseDetailPage,
	}))
);
const WarehouseEditPage = lazy(() =>
	import('@features/warehouses/WarehouseEditPage').then((m) => ({
		default: m.WarehouseEditPage,
	}))
);
const WarehouseCreatePage = lazy(() =>
	import('@features/warehouses/WarehouseCreatePage').then((m) => ({
		default: m.WarehouseCreatePage,
	}))
);
const ReportsPage = lazy(() =>
	import('@features/reports/ReportsPage').then((m) => ({
		default: m.ReportsPage,
	}))
);
const RoleListPage = lazy(() =>
	import('@features/roles/RoleListPage').then((m) => ({
		default: m.RoleListPage,
	}))
);
const RoleDetailPage = lazy(() =>
	import('@features/roles/RoleDetailPage').then((m) => ({
		default: m.RoleDetailPage,
	}))
);
const RoleCreatePage = lazy(() =>
	import('@features/roles/RoleCreatePage').then((m) => ({
		default: m.RoleCreatePage,
	}))
);
const RoleEditPage = lazy(() =>
	import('@features/roles/RoleEditPage').then((m) => ({
		default: m.RoleEditPage,
	}))
);
const SettingsPage = lazy(() =>
	import('@features/settings/SettingsPage').then((m) => ({
		default: m.SettingsPage,
	}))
);
const NotFoundPage = lazy(() => import('@app/errors/NotFound'));

export const router = createBrowserRouter([
	{
		path: '/login',
		element: <LoginPage />,
	},
	{
		path: '/',
		element: (
			<RequireAuth>
				<AdminLayout />
			</RequireAuth>
		),
		children: [
			{ index: true, element: <Navigate to='/dashboard' replace /> },
			{
				path: 'dashboard',
				element: <DashboardPage />,
			},
			{
				path: 'products',
				element: <ProductListPage />,
			},
			{
				path: 'products/new',
				element: (
					<RequireLevel minLevel={ROLE_LEVELS.MANAGER}>
						<ProductCreatePage />
					</RequireLevel>
				),
			},
			{
				path: 'products/:id',
				element: <ProductDetailPage />,
			},
			{
				path: 'products/:id/edit',
				element: (
					<RequireLevel minLevel={ROLE_LEVELS.MANAGER}>
						<ProductEditPage />
					</RequireLevel>
				),
			},
			{
				path: 'categories',
				element: <CategoryListPage />,
			},
			{
				path: 'categories/new',
				element: (
					<RequireLevel minLevel={ROLE_LEVELS.MANAGER}>
						<CategoryCreatePage />
					</RequireLevel>
				),
			},
			{
				path: 'categories/:id',
				element: <CategoryDetailPage />,
			},
			{
				path: 'categories/:id/edit',
				element: (
					<RequireLevel minLevel={ROLE_LEVELS.MANAGER}>
						<CategoryEditPage />
					</RequireLevel>
				),
			},
			{
				path: 'pallets',
				element: <PalletListPage />,
			},
			{
				path: 'pallets/new',
				element: (
					<RequireLevel minLevel={ROLE_LEVELS.MANAGER}>
						<PalletCreatePage />
					</RequireLevel>
				),
			},
			{
				path: 'pallets/:id',
				element: <PalletDetailPage />,
			},
			{
				path: 'pallets/:id/edit',
				element: (
					<RequireLevel minLevel={ROLE_LEVELS.MANAGER}>
						<PalletEditPage />
					</RequireLevel>
				),
			},
			{
				path: 'pallets/:id/history',
				element: <PalletHistoryPage />,
			},
			{
				path: 'locations',
				element: <LocationListPage />,
			},
			{
				path: 'locations/new/single',
				element: (
					<RequireLevel minLevel={ROLE_LEVELS.MANAGER}>
						<SingleLocationCreatePage />
					</RequireLevel>
				),
			},
			{
				path: 'locations/new/rack',
				element: (
					<RequireLevel minLevel={ROLE_LEVELS.MANAGER}>
						<LocationCreatePage />
					</RequireLevel>
				),
			},
			{
				path: 'locations/:id',
				element: <LocationDetailPage />,
			},
			{
				path: 'locations/:id/edit',
				element: (
					<RequireLevel minLevel={ROLE_LEVELS.MANAGER}>
						<LocationEditPage />
					</RequireLevel>
				),
			},
			{
				path: 'orders',
				element: <OrderListPage />,
			},
			{
				path: 'orders/new',
				element: (
					<RequireLevel minLevel={ROLE_LEVELS.MANAGER}>
						<OrderCreatePage />
					</RequireLevel>
				),
			},
			{
				path: 'orders/:id',
				element: <OrderDetailPage />,
			},
			{
				path: 'orders/:id/edit',
				element: (
					<RequireLevel minLevel={ROLE_LEVELS.MANAGER}>
						<OrderEditPage />
					</RequireLevel>
				),
			},
			{
				path: 'movements',
				element: <MovementListPage />,
			},
			{
				path: 'movements/:id',
				element: <MovementDetailPage />,
			},
			{
				path: 'stock-counts',
				element: (
					<RequireLevel minLevel={ROLE_LEVELS.MANAGER}>
						<StockCountListPage />
					</RequireLevel>
				),
			},
			{
				path: 'stock-counts/new',
				element: (
					<RequireLevel minLevel={ROLE_LEVELS.MANAGER}>
						<StockCountCreatePage />
					</RequireLevel>
				),
			},
			{
				path: 'stock-counts/:id',
				element: (
					<RequireLevel minLevel={ROLE_LEVELS.MANAGER}>
						<StockCountDetailPage />
					</RequireLevel>
				),
			},
			{
				path: 'expiry-alerts',
				element: <ExpiryAlertPage />,
			},
			{
				path: 'users',
				element: (
					<RequireLevel minLevel={ROLE_LEVELS.MANAGER}>
						<UserListPage />
					</RequireLevel>
				),
			},
			{
				path: 'users/new',
				element: (
					<RequireLevel minLevel={ROLE_LEVELS.MANAGER}>
						<UserCreatePage />
					</RequireLevel>
				),
			},
			{
				path: 'users/:id',
				element: (
					<RequireLevel minLevel={ROLE_LEVELS.MANAGER}>
						<UserDetailPage />
					</RequireLevel>
				),
			},
			{
				path: 'users/:id/edit',
				element: (
					<RequireLevel minLevel={ROLE_LEVELS.MANAGER}>
						<UserEditPage />
					</RequireLevel>
				),
			},
			{
				path: 'suppliers',
				element: (
					<RequireLevel minLevel={ROLE_LEVELS.MANAGER}>
						<SupplierListPage />
					</RequireLevel>
				),
			},
			{
				path: 'suppliers/new',
				element: (
					<RequireLevel minLevel={ROLE_LEVELS.MANAGER}>
						<SupplierCreatePage />
					</RequireLevel>
				),
			},
			{
				path: 'suppliers/:id',
				element: (
					<RequireLevel minLevel={ROLE_LEVELS.MANAGER}>
						<SupplierDetailPage />
					</RequireLevel>
				),
			},
			{
				path: 'suppliers/:id/edit',
				element: (
					<RequireLevel minLevel={ROLE_LEVELS.MANAGER}>
						<SupplierEditPage />
					</RequireLevel>
				),
			},
			{
				path: 'customers',
				element: (
					<RequireLevel minLevel={ROLE_LEVELS.MANAGER}>
						<CustomerListPage />
					</RequireLevel>
				),
			},
			{
				path: 'customers/new',
				element: (
					<RequireLevel minLevel={ROLE_LEVELS.MANAGER}>
						<CustomerCreatePage />
					</RequireLevel>
				),
			},
			{
				path: 'customers/:id',
				element: (
					<RequireLevel minLevel={ROLE_LEVELS.MANAGER}>
						<CustomerDetailPage />
					</RequireLevel>
				),
			},
			{
				path: 'customers/:id/edit',
				element: (
					<RequireLevel minLevel={ROLE_LEVELS.MANAGER}>
						<CustomerEditPage />
					</RequireLevel>
				),
			},
			{
				path: 'warehouses',
				element: (
					<RequireLevel minLevel={ROLE_LEVELS.MANAGER}>
						<WarehouseListPage />
					</RequireLevel>
				),
			},
			{
				path: 'warehouses/new',
				element: (
					<RequireLevel minLevel={ROLE_LEVELS.SUPER_ADMIN}>
						<WarehouseCreatePage />
					</RequireLevel>
				),
			},
			{
				path: 'warehouses/:id',
				element: (
					<RequireLevel minLevel={ROLE_LEVELS.MANAGER}>
						<WarehouseDetailPage />
					</RequireLevel>
				),
			},
			{
				path: 'warehouses/:id/edit',
				element: (
					<RequireLevel minLevel={ROLE_LEVELS.SUPER_ADMIN}>
						<WarehouseEditPage />
					</RequireLevel>
				),
			},
			{
				path: 'reports',
				element: (
					<RequireLevel minLevel={ROLE_LEVELS.MANAGER}>
						<ReportsPage />
					</RequireLevel>
				),
			},
			{
				path: 'roles',
				element: (
					<RequireLevel minLevel={ROLE_LEVELS.SUPER_ADMIN}>
						<RoleListPage />
					</RequireLevel>
				),
			},
			{
				path: 'roles/new',
				element: (
					<RequireLevel minLevel={ROLE_LEVELS.SUPER_ADMIN}>
						<RoleCreatePage />
					</RequireLevel>
				),
			},
			{
				path: 'roles/:id',
				element: (
					<RequireLevel minLevel={ROLE_LEVELS.SUPER_ADMIN}>
						<RoleDetailPage />
					</RequireLevel>
				),
			},
			{
				path: 'roles/:id/edit',
				element: (
					<RequireLevel minLevel={ROLE_LEVELS.SUPER_ADMIN}>
						<RoleEditPage />
					</RequireLevel>
				),
			},
			{
				path: 'settings',
				element: <SettingsPage />,
			},
		],
	},
	{
		path: '*',
		element: <NotFoundPage />,
	},
]);
